"use client";

import type { ReactNode } from "react";
import type { ChoghadiyaResponse, ChoghadiyaTimePeriod } from "@/lib/api/panchang";
import { ChoghadiyaSummary } from "./ChoghadiyaSummary";
import { ChoghadiyaLegend } from "./ChoghadiyaLegend";
import { ChoghadiyaTable } from "./ChoghadiyaTable";

/** Windows after the first one (the first is already shown); two only on Saturday. */
function laterWindows(windows: ChoghadiyaTimePeriod[] | undefined): ChoghadiyaTimePeriod[] {
  return (windows ?? []).slice(1);
}

interface ChoghadiyaResultProps {
  result: ChoghadiyaResponse;
  resultOffer: ReactNode;
}

export function ChoghadiyaResult({ result, resultOffer }: ChoghadiyaResultProps) {
  return (
    <div className="space-y-6">
      <ChoghadiyaSummary result={result} />

      {resultOffer}

      <ChoghadiyaLegend />

      <ChoghadiyaTable
        title="Day Choghadiya"
        titleHi="दिन का चौघड़िया"
        periods={result.day_choghadiya}
        timeRange={`${result.sunrise} - ${result.sunset}`}
      />

      <ChoghadiyaTable
        title="Night Choghadiya"
        titleHi="रात का चौघड़िया"
        periods={result.night_choghadiya}
        timeRange={`${result.sunset} - ${result.next_sunrise}`}
      />

      {/* Vaar Vela / Kaal Vela / Kaal Ratri */}
      {result.vaar_vela && (
        <div className="card-sacred p-4 space-y-3">
          <h3
            className="text-body font-bold"
            style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
          >
            Inauspicious Periods
            <span
              className="block text-caption font-normal mt-0.5"
              style={{ color: "var(--ish-text-muted)" }}
            >
              वार वेला · काल वेला · काल रात्रि
            </span>
          </h3>
          <p className="text-caption" style={{ color: "var(--ish-text-muted)" }}>
            Avoid starting new work during these periods. Tradition considers them unfavourable for
            auspicious activities.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              { label: "Vaar Vela", labelHi: "वार वेला", ...result.vaar_vela, also: [] },
              {
                label: "Kaal Vela",
                labelHi: "काल वेला",
                ...result.kaal_vela,
                also: laterWindows(result.kaal_vela_windows),
              },
              {
                label: "Kaal Ratri",
                labelHi: "काल रात्रि",
                ...result.kaal_ratri,
                also: laterWindows(result.kaal_ratri_windows),
              },
            ].map(({ label, labelHi, start_time, end_time, also }) => (
              <div
                key={label}
                className="rounded-lg p-3 text-center"
                style={{
                  background: "var(--color-error-bg)",
                  border: "1px solid var(--color-error-tint)",
                }}
              >
                <p
                  className="text-[11px] font-semibold uppercase tracking-wide mb-0.5"
                  style={{ color: "var(--color-error)" }}
                >
                  {label}
                </p>
                <p
                  className="text-[10px] mb-1"
                  style={{ color: "var(--color-error)", opacity: 0.7 }}
                >
                  {labelHi}
                </p>
                <p className="text-body-small font-bold" style={{ color: "var(--ish-dark)" }}>
                  {start_time} – {end_time}
                  {also.map((w) => (
                    <span key={w.start_time} className="block">
                      and {w.start_time} – {w.end_time}
                    </span>
                  ))}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
